import { useState } from "react";
import { FullScreenLoader } from "@/components/Spinner";
import { BottomSheet } from "@/components/BottomSheet";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, HelpCircle, Camera, ScanLine } from "lucide-react";

interface Step2Props {
  onNext: () => void;
  onBlock: () => void;
}

type VerifyState = "instructions" | "scanning" | "selfie" | "processing" | "success" | "retry" | "blocked" | "fraud";

export function Step2Identity({ onNext, onBlock }: Step2Props) {
  const [verifyState, setVerifyState] = useState<VerifyState>("instructions");
  const [showHelp, setShowHelp] = useState(false);

  const startVerification = () => {
    setVerifyState("scanning");
    // Simulate DL scan
    setTimeout(() => {
      setVerifyState("selfie");
    }, 2000);
  };

  const captureSelfie = () => {
    setVerifyState("processing");
    // Simulate verification
    setTimeout(() => {
      setVerifyState("success");
      setTimeout(() => onNext(), 1200);
    }, 2500);
  };

  if (verifyState === "processing") {
    return <FullScreenLoader message="Verifying your identity..." />;
  }

  if (verifyState === "success") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-in zoom-in">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <p className="text-lg font-medium text-green-700">Identity Verified</p>
      </div>
    );
  }

  if (verifyState === "blocked") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <XCircle className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold">We couldn't verify your identity</h2>
        <p className="text-gray-500">
          You've been referred to our team for assistance. We'll reach out within 1 business day.
        </p>
      </div>
    );
  }

  if (verifyState === "fraud") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <XCircle className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold">We're unable to proceed with your application</h2>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-6 py-6">
      <h2 className="text-xl font-bold mb-2">Verify Your Identity</h2>
      <p className="text-gray-500 text-sm mb-6">
        Scan your driver's license and take a quick selfie to verify your identity.
      </p>

      {/* Simulated Plaid widget area */}
      <div className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center min-h-[400px] gap-6 mb-4">
        {verifyState === "instructions" && (
          <>
            <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center">
              <ScanLine className="w-10 h-10 text-blue-500" />
            </div>
            <div className="text-center px-6">
              <p className="font-medium mb-1">Scan Your Driver's License</p>
              <p className="text-sm text-gray-500">
                Position the front of your DL within the frame
              </p>
            </div>
            <Button onClick={startVerification} className="w-48">
              Start Scanning
            </Button>
          </>
        )}

        {verifyState === "scanning" && (
          <>
            <div className="w-64 h-40 border-2 border-blue-400 rounded-xl relative overflow-hidden bg-gray-900/5">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-56 h-36 border border-blue-300 rounded-lg animate-pulse" />
              </div>
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 animate-[scan_2s_ease-in-out_infinite]" />
            </div>
            <p className="text-sm text-blue-600 animate-pulse">Scanning...</p>
          </>
        )}

        {verifyState === "selfie" && (
          <>
            <div className="w-32 h-32 bg-gray-200 rounded-full border-4 border-blue-400 flex items-center justify-center">
              <Camera className="w-12 h-12 text-blue-500" />
            </div>
            <div className="text-center px-6">
              <p className="font-medium mb-1">Take a Selfie</p>
              <p className="text-sm text-gray-500">
                Hold your phone at face level and look at the camera
              </p>
            </div>
            <Button onClick={captureSelfie} className="w-48">
              Capture
            </Button>
          </>
        )}

        {verifyState === "retry" && (
          <>
            <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center">
              <ScanLine className="w-10 h-10 text-yellow-500" />
            </div>
            <p className="font-medium">Let's try that again</p>
            <Button onClick={startVerification} className="w-48">
              Retry
            </Button>
          </>
        )}
      </div>

      <button
        type="button"
        onClick={() => setShowHelp(true)}
        className="flex items-center justify-center gap-2 text-sm text-blue-500 py-2"
      >
        <HelpCircle className="w-4 h-4" />
        Having trouble?
      </button>

      <BottomSheet
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="Tips for Verification"
      >
        <ul className="space-y-3 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">1.</span>
            Make sure your DL is well-lit
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">2.</span>
            Remove glasses for selfie
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">3.</span>
            Hold still during video
          </li>
        </ul>
        <Button className="w-full mt-4" onClick={() => setShowHelp(false)}>
          Got it
        </Button>
      </BottomSheet>
    </div>
  );
}
